import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { Login } from './pages/Login';
import { SignInComplete } from './pages/SignInComplete';
import { CivilRecords } from './pages/civil/CivilRecords';
import { Births } from './pages/civil/Births';
import { Certificates } from './pages/civil/Certificates';
import { ImmigrationApplications } from './pages/immigration/Applications';
import { Permits } from './pages/immigration/Permits';
import { PassportApplications } from './pages/passports/Applications';
import { Clearance } from './pages/police/Clearance';
import { CroRecords } from './pages/police/CroRecords';
import { Incidents } from './pages/police/Incidents';
import { Firearms } from './pages/police/Firearms';
import { FireDashboard } from './pages/fire/Dashboard';
import { FireApplications } from './pages/fire/Applications';
import { Inspections } from './pages/fire/Inspections';
import { Hydrants } from './pages/fire/Hydrants';
import { Inmates } from './pages/prisons/Inmates';
import { Visits } from './pages/prisons/Visits';
import { Probation } from './pages/prisons/Probation';
import { Shell, type NavHeader, type NavItem } from './ui/Shell';

const NAV: Array<NavItem | NavHeader> = [
  { header: 'Civil Registry — M01' },
  { to: '/civil/records', label: 'Civil records', icon: '⧉' },
  { to: '/civil/births', label: 'Births queue', icon: '👶' },
  { to: '/civil/certificates', label: 'Certificates', icon: '▤' },

  { header: 'Immigration — M03' },
  { to: '/immigration/applications', label: 'Permit applications', icon: '📋', exact: false },
  { to: '/immigration/permits', label: 'Permits', icon: '🪪', exact: false },

  { header: 'Passports — M04' },
  { to: '/passports/applications', label: 'Applications', icon: '📘', exact: false },

  { header: 'Police — M05' },
  { to: '/police/clearance', label: 'Clearance certificates', icon: '✔', exact: false },
  { to: '/police/cro', label: 'CRO records', icon: '🗂' },
  { to: '/police/incidents', label: 'Incidents', icon: '🚨', exact: false },
  { to: '/police/firearms', label: 'Firearms licences', icon: '🔫', exact: false },

  { header: 'Fire & Rescue — M06' },
  { to: '/fire/dashboard', label: 'Dashboard', icon: '◔' },
  { to: '/fire/applications', label: 'Cert applications', icon: '📄', exact: false },
  { to: '/fire/inspections', label: 'Inspections', icon: '🔥', exact: false },
  { to: '/fire/hydrants', label: 'Hydrants', icon: '🚰', exact: false },

  { header: 'Prisons & Probation — M07' },
  { to: '/prisons/inmates', label: 'Inmates', icon: '🔒', exact: false },
  { to: '/prisons/visits', label: 'Visits', icon: '🧑‍🤝‍🧑', exact: false },
  { to: '/prisons/probation', label: 'Probation caseload', icon: '📝', exact: false },
];

const ROLE_TO_LANDING: Record<string, string> = {
  REGISTRAR: '/civil/records',
  DEPUTY_REGISTRAR: '/civil/records',
  IMMIGRATION_OFFICER: '/immigration/applications',
  SENIOR_IO: '/immigration/applications',
  PASSPORT_OFFICER: '/passports/applications',
  VETTING_OFFICER: '/passports/applications',
  PRODUCTION: '/passports/applications',
  STATION_OFFICER: '/police/incidents',
  CRO_OFFICER: '/police/clearance',
  FIREARMS_REGISTRAR: '/police/firearms',
  FIRE_INSPECTOR: '/fire/dashboard',
  CHIEF_FIRE_OFFICER: '/fire/dashboard',
  CUSTODIAL_ADMIN: '/prisons/inmates',
  PROBATION_OFFICER: '/prisons/probation',
};

export function App() {
  const { persona } = useAuth();

  if (!persona) {
    return (
      <Routes>
        <Route path="/signin/complete" element={<SignInComplete />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  // Land each persona somewhere useful for their primary duty rather than
  // always defaulting to the first workspace in the sidebar.
  const landing = persona.roles.map((r) => ROLE_TO_LANDING[r]).find(Boolean) ?? '/civil/records';

  return (
    <Routes>
      <Route element={<Shell title={persona.org} items={NAV} />}>
        <Route index element={<Navigate to={landing} replace />} />

        <Route path="civil/records" element={<CivilRecords />} />
        <Route path="civil/births" element={<Births />} />
        <Route path="civil/certificates" element={<Certificates />} />

        <Route path="immigration/applications" element={<ImmigrationApplications />} />
        <Route path="immigration/permits" element={<Permits />} />

        <Route path="passports/applications" element={<PassportApplications />} />

        <Route path="police/clearance" element={<Clearance />} />
        <Route path="police/cro" element={<CroRecords />} />
        <Route path="police/incidents" element={<Incidents />} />
        <Route path="police/firearms" element={<Firearms />} />

        <Route path="fire/dashboard" element={<FireDashboard />} />
        <Route path="fire/applications" element={<FireApplications />} />
        <Route path="fire/inspections" element={<Inspections />} />
        <Route path="fire/hydrants" element={<Hydrants />} />

        <Route path="prisons/inmates" element={<Inmates />} />
        <Route path="prisons/visits" element={<Visits />} />
        <Route path="prisons/probation" element={<Probation />} />
      </Route>

      <Route path="*" element={<Navigate to={landing} replace />} />
    </Routes>
  );
}
